import React, { useState, useEffect } from 'react';
import { LinkIcon, DocumentIcon, EyeIcon, EyeSlashIcon, PencilIcon } from '@heroicons/react/24/outline';
import Modal from '../common/Modal';
import { Contract } from '../../types/contract';
import { ShareSettings } from '../../types/common';
import { useLanguage } from '../../contexts/LanguageContext';
import { useContracts } from '../../hooks/useContracts';
import toast from 'react-hot-toast';
import '../../styles/toggle-switch.css';

// Segédfüggvény a dátum formázásához, amely kezeli az undefined értékeket
const getFormattedDate = (date: ShareSettings['lastViewed']) => {
  if (!date) return '';

  try {
    // Ha a date objektumnak van toDate metódusa (Firestore Timestamp)
    if (date && typeof date === 'object' && 'toDate' in date && typeof date.toDate === 'function') {
      const jsDate = date.toDate();
      return `${jsDate.toLocaleDateString()} ${jsDate.toLocaleTimeString()}`;
    }

    // Ha a date egy JavaScript Date objektum
    if (date instanceof Date) {
      return `${date.toLocaleDateString()} ${date.toLocaleTimeString()}`;
    }

    // Ha string vagy egyéb formátum, próbáljuk meg Date objektummá konvertálni
    const jsDate = new Date(date as any);
    return `${jsDate.toLocaleDateString()} ${jsDate.toLocaleTimeString()}`;
  } catch (error) {
    console.error('Hiba a dátum formázása során:', error);
    return '';
  }
};

interface ShareContractModalProps {
  isOpen: boolean;
  onClose: () => void;
  contract: Contract | null;
}

const ShareContractModal: React.FC<ShareContractModalProps> = ({
  isOpen,
  onClose,
  contract
}) => {
  const { t } = useLanguage();
  const { updateContract, refreshContracts } = useContracts();

  const [isPasswordProtected, setIsPasswordProtected] = useState(true);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [shareLinkCreated, setShareLinkCreated] = useState(false);
  const [shareLink, setShareLink] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [validUntil, setValidUntil] = useState<string>('');
  const [isEditing, setIsEditing] = useState(false);

  // Modal megnyitásakor frissítsük a szerződéseket
  useEffect(() => {
    if (isOpen) {
      refreshContracts();
    }
  }, [isOpen, refreshContracts]);

  // Ellenőrizzük, hogy van-e már megosztási beállítás
  useEffect(() => {

    if (contract && contract.shareSettings) {

      // Ha már létezik megosztás, használjuk azt
      const { shareId, isPasswordProtected: hasPassword, password: savedPassword, validUntil: savedValidUntil, viewCount } = contract.shareSettings;

      // Beállítjuk a létező adatokat
      setIsPasswordProtected(hasPassword);
      if (hasPassword && savedPassword) {
        setPassword(savedPassword);
      }

      if (savedValidUntil) {
        try {
          // Dátum konvertálása input[type=datetime-local] formátumra (YYYY-MM-DDThh:mm)
          const date = savedValidUntil instanceof Date ? savedValidUntil : new Date(savedValidUntil);
          const formattedDate = date.toISOString().slice(0, 16);
          setValidUntil(formattedDate);
        } catch (error) {
          console.error("Hiba a dátum konvertálása során:", error);
        }
      }

      // Generáljuk a megosztási linket a meglévő shareId-vel
      const baseUrl = window.location.origin;
      const link = `${baseUrl}/shared/contracts/${contract.id}?share=${shareId}`;
      setShareLink(link);

      // Jelezzük, hogy a link már létezik
      setShareLinkCreated(true);
      setIsEditing(false);
    } else {
      // Ha még nincs megosztás, alapértelmezett beállítások
      setIsPasswordProtected(true);
      setPassword('');
      setValidUntil('');
      setShareLink('');
      setShareLinkCreated(false);
      setIsEditing(false);
    }
  }, [contract, isOpen]); // isOpen hozzáadása a függőségi tömbhöz

  // Metódus a megosztási link generálásához vagy frissítéséhez
  const generateOrUpdateShareLink = async () => {
    if (!contract) return;

    setIsLoading(true);
    try {
      let shareId;

      // Ha már létezik megosztás, használjuk a meglévő shareId-t
      if (contract.shareSettings && contract.shareSettings.shareId && !isEditing) {
        shareId = contract.shareSettings.shareId;
      } else {
        // Egyébként generálunk egy újat
        shareId = Math.random().toString(36).substring(2, 15);
      }

      const baseUrl = window.location.origin;

      // Generált link formátuma
      const link = `${baseUrl}/shared/contracts/${contract.id}?share=${shareId}`;
      setShareLink(link);
      setShareLinkCreated(true);

      // Adatok mentése az adatbázisba
      const shareData = {
        shareId,
        isPasswordProtected,
        password: isPasswordProtected ? password : null,
        validUntil: validUntil ? new Date(validUntil) : null,
        createdAt: contract.shareSettings?.createdAt || new Date(),
        viewCount: contract.shareSettings?.viewCount || 0,
        lastViewed: contract.shareSettings?.lastViewed || null
      };

      // A szerződés frissítése a megosztási adatokkal
      await updateContract(contract.id, {
        'shareSettings': shareData
      } as any);

      // Frissítsük a szerződéseket a helyes megjelenítéshez
      await refreshContracts();

      // Ha szerkesztési módban voltunk, akkor lépjünk ki belőle
      setIsEditing(false);

    } catch (error) {
      console.error('Hiba a megosztási link létrehozásakor:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Megosztási link másolása a vágólapra
  const copyLinkToClipboard = () => {
    navigator.clipboard.writeText(shareLink)
      .then(() => {
        toast.success(t("contracts.shared") || "A link másolva a vágólapra!");
        onClose();
      })
      .catch(err => {
        console.error("Nem sikerült másolni a vágólapra:", err);
        toast.error(t("common.messages.copyError") || "Nem sikerült másolni a vágólapra!");
      });
  };

  // Szerkesztési mód bekapcsolása
  const handleEdit = () => {
    setIsEditing(true);
  };

  // Szerkesztés visszavonása
  const handleCancelEdit = () => {
    // Visszaállítjuk az eredeti értékeket
    if (contract && contract.shareSettings) {
      setIsPasswordProtected(contract.shareSettings.isPasswordProtected);
      setPassword(contract.shareSettings.password || '');

      if (contract.shareSettings.validUntil) {
        try {
          const date = contract.shareSettings.validUntil instanceof Date
            ? contract.shareSettings.validUntil
            : new Date(contract.shareSettings.validUntil);
          setValidUntil(date.toISOString().slice(0, 16));
        } catch (error) {
          console.error("Hiba a dátum konvertálása során:", error);
        }
      } else {
        setValidUntil('');
      }
    }

    setIsEditing(false);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t("contracts.shareModal.title") || "Dokumentum megosztása"}
      size="lg"
    >
      <div className="p-6 space-y-5">
        {!shareLinkCreated || isEditing ? (
          <>
            <div className="bg-white dark:bg-gray-700 rounded-lg p-4 mb-5 border border-gray-200 dark:border-gray-600 shadow-sm">
              <div className="flex items-center">
                <DocumentIcon className="h-7 w-7 text-emerald-600 dark:text-emerald-400 mr-3" />
                <div>
                  <h3 className="text-gray-800 dark:text-gray-200 font-medium">{contract?.name}</h3>
                  <p className="text-gray-500 dark:text-gray-400 text-sm">{t("contracts.types." + contract?.type)}</p>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <label className="text-gray-700 dark:text-gray-300">
                  {t("contracts.shareModal.passwordProtection") || "Jelszavas védelem"}
                </label>
                <div>
                  {/* CSS osztályokkal megvalósított toggle switch */}
                  <label className="switch">
                    <input
                      type="checkbox"
                      checked={isPasswordProtected}
                      onChange={() => setIsPasswordProtected(!isPasswordProtected)}
                    />
                    <span className="slider"></span>
                  </label>
                </div>
              </div>

              {isPasswordProtected && (
                <div className="space-y-2">
                  <label htmlFor="password" className="block text-sm font-medium text-gray-600 dark:text-gray-400">
                    {t("contracts.shareModal.password") || "Jelszó"}
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      id="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="mt-1 block w-full p-2.5 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 focus:ring-teal-500 focus:border-teal-500"
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 flex items-center pr-3"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? (
                        <EyeSlashIcon className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                      ) : (
                        <EyeIcon className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                      )}
                    </button>
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <label htmlFor="valid-until" className="block text-sm font-medium text-gray-600 dark:text-gray-400">
                  {t("contracts.shareModal.validUntil") || "Érvényesség vége (opcionális)"}
                </label>
                <input
                  type="datetime-local"
                  id="valid-until"
                  value={validUntil}
                  onChange={(e) => setValidUntil(e.target.value)}
                  className="mt-1 block w-full p-2.5 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 focus:ring-teal-500 focus:border-teal-500"
                />
              </div>

              <div className="pt-4 flex justify-end space-x-3">
                {isEditing ? (
                  <button
                    type="button"
                    onClick={handleCancelEdit}
                    className="px-4 py-2 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-300 dark:hover:bg-gray-500 transition-colors"
                  >
                    {t("common.cancel") || "Mégse"}
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={onClose}
                    className="px-4 py-2 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-300 dark:hover:bg-gray-500 transition-colors"
                  >
                    {t("common.cancel") || "Mégse"}
                  </button>
                )}
                <button
                  type="button"
                  onClick={generateOrUpdateShareLink}
                  disabled={isLoading || (isPasswordProtected && !password)}
                  className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-teal-600 hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500 disabled:bg-teal-300 dark:disabled:bg-teal-600"
                >
                  {isLoading ? (
                    <span>{t("common.loading") || "Betöltés..."}</span>
                  ) : isEditing ? (
                    <span>{t("contracts.shareModal.updateLink") || "Megosztási beállítások frissítése"}</span>
                  ) : (
                    <span>{t("contracts.shareModal.createLink") || "Megosztási link létrehozása"}</span>
                  )}
                </button>
              </div>
            </div>
          </>
        ) : (
          <div className="space-y-5">
            <div className="bg-white dark:bg-gray-700 rounded-lg p-4 border border-gray-200 dark:border-gray-600 shadow-sm">
              <p className="text-gray-600 dark:text-gray-400 mb-2">{t("contracts.shareModal.linkCreated") || "Megosztási link létrehozva:"}</p>
              <div className="flex items-center bg-gray-50 dark:bg-gray-600 rounded-md p-2.5 border border-gray-200 dark:border-gray-500">
                <input
                  type="text"
                  readOnly
                  value={shareLink}
                  className="flex-grow bg-transparent border-0 text-gray-700 dark:text-gray-300 focus:ring-0 text-sm"
                />
                <button
                  type="button"
                  onClick={copyLinkToClipboard}
                  className="ml-2 text-teal-600 dark:text-teal-400 hover:text-teal-700 dark:hover:text-teal-300"
                >
                  <LinkIcon className="h-5 w-5" />
                </button>
              </div>

              {/* Megtekintési adatok megjelenítése, ha elérhetők */}
              {contract?.shareSettings?.viewCount !== undefined && (
                <div className="mt-3 flex items-center text-gray-500 dark:text-gray-400 text-sm">
                  <EyeIcon className="h-4 w-4 mr-2" />
                  <span>{t("contracts.viewCount", { count: contract.shareSettings.viewCount })}</span>
                  {contract.shareSettings.lastViewed && (
                    <span className="ml-3 text-xs">
                      {t("contracts.lastViewed")}: {getFormattedDate(contract.shareSettings.lastViewed)}
                    </span>
                  )}
                </div>
              )}
            </div>

            <div className="bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 p-4 rounded-md border border-blue-200 dark:border-blue-800">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-blue-500 dark:text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3 flex-1 md:flex md:justify-between">
                  <p className="text-sm">
                    {isPasswordProtected
                      ? t("contracts.shareModal.linkPasswordInfo") || "Ez a link jelszóval védett. A címzettnek meg kell adnia a jelszót a megtekintéshez."
                      : t("contracts.shareModal.linkInfo") || "Bárki, aki rendelkezik ezzel a linkkel, megtekintheti a dokumentumot."}
                  </p>
                </div>
              </div>
            </div>

            <div className="pt-4 flex justify-end space-x-3">
              <button
                type="button"
                onClick={handleEdit}
                className="px-4 py-2 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-300 dark:hover:bg-gray-500 transition-colors flex items-center"
              >
                <PencilIcon className="h-5 w-5 mr-2" />
                {t("common.edit") || "Szerkesztés"}
              </button>

              <button
                type="button"
                onClick={copyLinkToClipboard}
                className="px-4 py-2 bg-teal-600 text-white rounded-md hover:bg-teal-700 transition-colors flex items-center"
              >
                <LinkIcon className="h-5 w-5 mr-2" />
                {t("contracts.shareModal.copyLink") || "Link másolása"}
              </button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default ShareContractModal; 